import React from "react"
import { DateTime } from "luxon"

import { Date } from "@tenforce/product-fields"

import { GlobalState } from "../../../../store"
import { useAppDispatch, useAppSelector } from "../../../../hooks"

import { setCurrentFieldInEditMode, updateField } from "../../slice"
import HTMLAndInternalLinkParser from "../common/HTMLAndInternalLinkParser"
import { Labels } from "../../../../util/Translations"
import { IDateField } from "../../index"
import { DueDateCode } from "../../../../apis/utils/constants"

export interface Props {
    field: IDateField
}

export const DateField = ({ field }: Props): JSX.Element => {
    const dispatch = useAppDispatch()
    const {
        isLoading: isFieldLoading,
        currentFieldInEditMode = "",
        statusObject,
    } = useAppSelector((state: GlobalState) => state.fields)

    const {
        position: _position,
        odataType: _odataType,
        value,
        locale,
        timeZone,
        itemType,
        ...fieldProps
    } = field

    const { ClickHereToEdit, PleaseEnterAValidDateFormat } = Labels

    const isLoading = fieldProps.isLoading || isFieldLoading

    const isDisabled =
        fieldProps.isDisabled ||
        !["", fieldProps.fieldId].includes(currentFieldInEditMode)

    const shouldHighlightIfDatePassed =
        fieldProps.fieldId === DueDateCode &&
        (statusObject?.isIndicatingItemIsActive ?? false)

    const shouldShowTimeComponent =
        itemType.toLowerCase() !== "meeting" &&
        fieldProps.fieldId === DueDateCode

    return (
        <Date
            {...fieldProps}
            description={
                <HTMLAndInternalLinkParser
                    content={(field?.description as string) ?? ""}
                />
            }
            value={value}
            locale={locale}
            timeZone={timeZone}
            isLoading={isLoading}
            placeholder={ClickHereToEdit}
            onSave={async (newValue: string): Promise<void> => {
                if (newValue === value) {
                    return
                }

                const finalDate = shouldHighlightIfDatePassed
                    ? DateTime.fromISO(newValue).startOf("day").toISO()
                    : newValue

                await dispatch(
                    updateField({
                        field,
                        newValue: finalDate,
                    }),
                ).unwrap()
            }}
            labels={{
                PleaseEnterAValidDate: `${PleaseEnterAValidDateFormat} ${
                    locale === "en" ? "MM/DD/YYYY" : "DD/MM/YYYY"
                }`,
            }}
            isDisabled={isDisabled}
            onFieldEnterEditMode={(fieldId): void => {
                dispatch(
                    setCurrentFieldInEditMode(fieldId ?? fieldProps.fieldId),
                )
            }}
            onFieldExitEditMode={(): void => {
                dispatch(setCurrentFieldInEditMode(""))
            }}
            shouldHighlightIfDatePassed={shouldHighlightIfDatePassed}
            shouldShowTimeComponent={shouldShowTimeComponent}
        />
    )
}

export default DateField
